import {
  endSessionApi,
  getAccessTokenFromRefreshTokenAPI,
  getParsedAccessToken,
  processTokenData
} from '../components/callback/callBack.service';
import { LoginConstants } from '../config/keyClock.config';
import { normalizeRoutePath, routeGuard } from '../config/route-guards';
import { parseJwt } from '../util/jwt';
import { hydrateEntityScope } from './entity-scope-persistence';
import { storeIntendedUrl } from './intended-navigation';
import { notifyTokenWrittenHere } from './scope-broadcast';
import { getStore } from '../store/get-store';
// `resolveEntityScope` is imported dynamically inside bootstrapAuth, not
// statically here — see the comment at that call site for why a static
// import closes a module-load cycle back onto this file.

// Refresh threshold: if the token has fewer than this many seconds left when a
// request goes out, refresh first. The window must be large enough to cover
// network latency between the browser's expiry check and the server's
// validation, and short enough that we don't refresh on every request.
const REFRESH_THRESHOLD_SECONDS = 60;

// Single in-flight refresh promise shared by every caller (axios interceptor,
// RTK Query base query, manual entity-switch sites). Funnelling concurrent
// refresh attempts through one promise serves two purposes:
//   1. Avoids redundant Keycloak roundtrips when multiple queries hit a
//      near-expired token simultaneously.
//   2. Prevents a localStorage race where two refreshes resolve out of order
//      and the older response overwrites the newer token blob.
// Refresh tokens are reusable: realm `planomatic` runs revokeRefreshToken=false
// in QA and prod (measured through the Keycloak admin API, 13 Oct 2026; Max
// Reuse is ignored while revocation is off, and the Connect client has no
// override). So the singleton is not what keeps a session alive — but the
// efficiency and race-avoidance reasons stand. The singleton is per TAB; for a
// lost race BETWEEN tabs, should revocation ever be turned on, see the
// adoption in refreshTokenOnce.
let refreshPromise: Promise<string> | null = null;

/**
 * Fire (or join) the single in-flight refresh-token call.
 *
 * Resolves with the new access token. Rejects if no refresh token is stored,
 * or if the refresh endpoint itself fails — callers are responsible for
 * deciding whether to surface the error or trigger an end-session.
 *
 * <p>A failed refresh is NOT reported while another tab has meanwhile stored a
 * newer, unexpired token (DV-3421 review round 6, item F): this tab lost a race
 * it did not need to win, and resolves with that token instead. Under refresh
 * token rotation (revocation on) that is exactly what a lost race looks like —
 * `invalid_grant` for the refresh token the winner consumed — and reporting it
 * would make `baseQueryWithAuth` end a session that is healthy in storage,
 * logging the user out of every tab.
 */
export const refreshTokenOnce = (): Promise<string> => {
  if (refreshPromise) return refreshPromise;

  const tokenData = getParsedAccessToken();
  if (!tokenData?.refresh_token) {
    return Promise.reject(new Error('No refresh token available'));
  }

  const startedWith = tokenData.access_token;
  refreshPromise = getAccessTokenFromRefreshTokenAPI(tokenData.refresh_token)
    .then((response) => {
      // DV-3573: false — a refresh must not re-derive currentContext/role from
      // the claim. See processTokenData's doc.
      // May decline the write when another tab has already stored a NEWER
      // token for this user; the read below then returns that one.
      processTokenData(response.data, false);
      // The other tabs hear about this write from `storage`; this one does not
      // (DV-3421 review round 5, item 1). Its root may have moved too.
      notifyTokenWrittenHere();
      // processTokenData just persisted a token (this one, or a newer one it
      // kept), so the read can't be null.
      return getParsedAccessToken()!.access_token;
    })
    .catch((error: unknown) => {
      const adopted = tokenStoredSince(startedWith);
      if (adopted) return adopted;
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * The stored access token when it is not `startedWith`, belongs to the SAME
 * USER, and still has life left — another tab refreshed this user's session
 * while this one's refresh was failing. Else null.
 *
 * <p>Same user is required (review of 0a17c730, item 2): a different person may
 * have logged in from another tab meanwhile, and adopting their token would
 * have `baseQueryWithAuth` retry this tab's request — a mutation included — as
 * them. That case fails as before; the reconciler reloads the tab.
 */
const tokenStoredSince = (startedWith: string | undefined): string | null => {
  const stored = getParsedAccessToken()?.access_token;
  if (!startedWith || !stored || stored === startedWith) return null;
  try {
    const before = parseJwt(startedWith);
    const now = parseJwt(stored);
    if (typeof now.sub !== 'string' || now.sub !== before.sub) return null;
    return now.exp > Math.floor(Date.now() / 1000) ? stored : null;
  } catch {
    return null;
  }
};

/**
 * A refresh guaranteed to have STARTED after this call IN THIS TAB — never a
 * join onto one already in flight here.
 *
 * <p>DV-3421 review finding 1. `refreshTokenOnce()` deliberately joins the
 * single in-flight promise, which is right for the interceptor's "I need a
 * usable token" case and wrong for ours: a refresh begun a moment before
 * `POST /users/context` was written mints its claims from the OLD attributes.
 * The switch would then report success holding a token still anchored to the
 * previous company — the exact "looks switched, everything denied" state this
 * ticket exists to remove, and invisible because the guard's refusals are
 * silent in the UI.
 *
 * <p>So: let any running refresh settle (its outcome is irrelevant — we are
 * replacing it, and letting it reject unhandled would surface a failure that
 * belongs to somebody else's call), then start a fresh one.
 *
 * <p><b>Only within this tab</b> (review round 5, item 6). Another tab's
 * refresh, begun before the context write and answered after this one, would
 * write a token minted from the old attributes into shared storage. The writer
 * refuses that (`processTokenData` keeps a stored same-user token issued
 * LATER than the incoming one). The one gap is a tie within the same issue
 * second, which `reconcileScope` catches with one verifying refresh.
 */
export const refreshTokenFresh = async (): Promise<string> => {
  if (refreshPromise) {
    await refreshPromise.catch(() => undefined);
  }
  return refreshTokenOnce();
};

/**
 * Returns a valid access token, refreshing proactively if it expires within
 * REFRESH_THRESHOLD_SECONDS. Returns null when no token is stored at all
 * (e.g. unauthenticated public pages); throws when a refresh attempt fails.
 */
export const getValidAccessToken = async (): Promise<string | null> => {
  const tokenData = getParsedAccessToken();
  if (!tokenData?.access_token) return null;

  const parsed = parseJwt(tokenData.access_token);
  const secondsUntilExpiry = parsed.exp - Math.floor(Date.now() / 1000);

  if (secondsUntilExpiry >= REFRESH_THRESHOLD_SECONDS) {
    return tokenData.access_token;
  }

  return refreshTokenOnce();
};

// localStorage keys that endSession preserves across its storage wipe.
// Logout/session-death otherwise clears everything. Only state a *genuine
// deep-link* needs to survive between capture and post-login consumption is
// kept here — deliberately NOT the org scope (selectedOrg / selectedEntity /
// parentEntityId / ...), so a different user logging in next can never
// inherit the previous user's entity context.
//
//   intendedUrl   deep-link destination captured by the unauthenticated-
//                 access path (bootstrapAuth / PrivateRoute); consumed by
//                 the org picker or useOrgSelection.computePostAuthTarget.
//   placeOrder    mid-order flow flag owned by the place-order feature;
//                 preserved as-is (its lifecycle is unrelated to org scope).
//   deniedEntityTarget
//                 the negative half of intendedUrl (DV-3012): the entityUUID
//                 that stored deep link was already refused for. Preserved
//                 deliberately, and only because intendedUrl is: keeping the
//                 link without keeping the refusal makes the next login
//                 re-resolve the same unreachable target and bounce to
//                 /error again. It cannot leak scope to the next user —
//                 it is not scope, it is a single refused id, and its only
//                 effect is to RETIRE a deep link the next user did not ask
//                 for either.
const SESSION_PRESERVED_KEYS = [
  'intendedUrl',
  'placeOrder',
  'deniedEntityTarget',
] as const;

/**
 * Canonical end-session: fire the backend logout, unconditionally clear
 * local state, restore the small preserved-keys allowlist, then redirect.
 * The `.finally` guarantees cleanup runs even when endSessionApi rejects
 * (network blip, expired session on the server, etc.).
 *
 * endSession does NOT capture the current page — logging out (or a session
 * dying) must not return the user to where they were on the next login.
 * Only an already-captured deep-link (intendedUrl) and the place-order flag
 * survive the wipe (see SESSION_PRESERVED_KEYS); everything else — token,
 * RBAC, org scope, cart drafts, cached lists — is cleared so the next login
 * starts from a clean, identity-correct state.
 */
export const endSession = (): void => {
  const preserved: Record<string, string> = {};
  for (const key of SESSION_PRESERVED_KEYS) {
    const value = localStorage.getItem(key);
    if (value !== null) preserved[key] = value;
  }
  endSessionApi()
    .finally(() => {
      localStorage.clear();
      sessionStorage.clear();
      for (const [key, value] of Object.entries(preserved)) {
        localStorage.setItem(key, value);
      }
      window.location.assign('/');
    })
    // Swallow the original rejection. The .finally above already ran the
    // local cleanup; surfacing the network error to an unhandled-rejection
    // handler serves no purpose now that the tab is navigating away.
    .catch(() => {});
};

/**
 * Outcome of the pre-render auth bootstrap. `render` means the app can mount
 * (token valid or refreshed successfully — and, if the route needs an
 * entity, correctly scoped into one — or the route's `auth` requirement is
 * not `required` and there's no token). `redirecting` means a navigation has
 * been kicked off and the caller should keep a full-page spinner up until
 * the page actually swaps. `no-organizations` (DV-3175) is a dead end: the
 * user is authenticated but belongs to no organisation, and the caller
 * renders `<NoOrganizations />` rather than navigating.
 */
export type BootstrapOutcome = 'render' | 'redirecting' | 'no-organizations';

/**
 * True when an unknown error looks like an HTTP response from the server
 * (Axios attaches a `response` object on 4xx/5xx). False for transport-level
 * failures (network down, CORS, DNS) where Axios has no response. Used by
 * bootstrap to decide between "session genuinely gone — sign the user out"
 * and "transient connectivity blip — degrade gracefully and let runtime
 * 401 handlers retry when we're back online."
 */
const isServerResponseError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const maybeResponse = (err as { response?: unknown }).response;
  return maybeResponse != null && typeof maybeResponse === 'object';
};

/**
 * Pre-render auth bootstrap. Run once at App mount, before any children
 * (including SSE / RTK Query subscribers) get a chance to fire requests.
 *
 * Decision tree:
 *   1. No stored access token → clear the key. If the route's `auth`
 *      requirement is not `required`, render (the route handles its own auth
 *      state — Loader kicks off the Keycloak login flow). Otherwise capture
 *      intendedUrl and redirect to `/`.
 *   2. Token blob missing the refresh_token field → broken state with no
 *      path to recovery; endSession() and let it redirect.
 *   3. getValidAccessToken() succeeds (token still fresh, or silently
 *      refreshed) → the token is known good; resolve the entity scope the
 *      route needs (DV-3012, spec §3.3) and map its outcome (step 6).
 *   4. Refresh failed with an HTTP response (Keycloak rejected: refresh
 *      token expired / revoked / session terminated server-side) →
 *      endSession() and redirect. Scope resolution is skipped: there is no
 *      valid token left to scope.
 *   5. Refresh failed without an HTTP response (network / CORS / DNS) →
 *      degrade gracefully: log a warning, render with the stored token, and
 *      let the runtime 401→refresh→endSession chain pick up if the token
 *      is actually dead. We do NOT punish offline users with a forced
 *      logout, and scope resolution is skipped here too — it would only
 *      fail the same way, against the same unreachable backend.
 *   6. resolveEntityScope() resolves: `ready`/`switching` → render (already
 *      correctly scoped); `navigating` → redirect (a full-page trip is
 *      underway); `no-organizations` → the DV-3175 dead end, passed through
 *      as-is; `denied` → redirect, the trip to `/error?reason=…` having
 *      already been started by the step itself (spec §3.4 — it owns
 *      navigation, and it is no longer this module's only caller).
 */
export const bootstrapAuth = async (
  currentPath: string,
): Promise<BootstrapOutcome> => {
  // One-time housekeeping that used to live in App.tsx's mount effect.
  // The static permissionRolesMap is the single source of truth for RBAC;
  // any stale `permissions` key in localStorage is unused and only
  // confuses future debugging.
  localStorage.removeItem('permissions');

  const guard = routeGuard(currentPath);
  const token = getParsedAccessToken();

  if (!token?.access_token) {
    // Either no token, or the stored blob was malformed and
    // getParsedAccessToken cleared it. Remove the key defensively, then
    // decide render vs. redirect from the route's auth requirement below.
    localStorage.removeItem(LoginConstants.ACCESS_TOKEN);
    // Only an auth-required route bounces to login. 'any' routes handle their
    // own auth state (Loader kicks off the Keycloak flow at '/'), and
    // 'anonymous' routes are meant to be reached without a token.
    // Replaces the `publicRoutes` array — one declaration, in route-guards.ts
    // (spec §5.1 / D9).
    if (guard.auth !== 'required') return 'render';
    storeIntendedUrl();
    window.location.assign('/');
    return 'redirecting';
  }

  // Adopt the persisted scope before anything reads it — resolveEntityScope
  // and every consumer read the slice, not storage (DV-3013 spec §3.2).
  //
  // Only once there IS a token (DV-3573 review). With none there is no user to
  // check a stamped scope against, so hydrating here loaded whoever's scope
  // the tab last held — the previous user's, after a logout elsewhere — which
  // `/callback` then held while the new user's token landed, and reconcile
  // read as a user change. Nothing token-less needs a scope: `/callback`
  // re-hydrates through `useScopeBootstrap` once its token is stored.
  hydrateEntityScope(getStore());

  if (!token.refresh_token) {
    // Access token without a refresh token is a broken state — we can't
    // silently recover, and we shouldn't trust the stored access token to
    // outlive its current TTL. Capture any deep-link target first (so a
    // bounced user still lands there after re-auth), then terminate.
    storeIntendedUrl();
    endSession();
    return 'redirecting';
  }

  try {
    // Returns the current token if exp > REFRESH_THRESHOLD_SECONDS away,
    // otherwise silently refreshes via refreshTokenOnce.
    await getValidAccessToken();
  } catch (err) {
    if (isServerResponseError(err)) {
      // Refresh rejected server-side (expired / revoked). Capture the
      // deep-link target before the logout wipe so it survives re-auth.
      storeIntendedUrl();
      endSession();
      return 'redirecting';
    }
    console.warn(
      'Auth bootstrap: refresh failed without an HTTP response (likely offline). ' +
        'Rendering with the stored token; runtime 401 handlers will retry.',
      err,
    );
    // Deliberately skip scope resolution: it would only fail the same way,
    // and the runtime 401 chain is the recovery path.
    return 'render';
  }

  // The token is known good. One more step before anything renders: make
  // sure the scope this route needs is the scope the token actually carries
  // (DV-3012, spec §3.3).
  //
  // Dynamic import, deliberately, as an INTERIM localization (the remaining
  // fix is banked in docs/engineering-backlog.md, "Tech debt"):
  // entity-scope-resolution.ts dispatches against the
  // Redux store (via entity-scope-api.ts's store-dispatch seams), and the
  // store's reducer map pulls in every RTK Query api slice, each of which
  // imports api-base.ts's baseQueryWithAuth — which imports THIS module for
  // getValidAccessToken/endSession. A static import here would close that
  // loop (auth-token → entity-scope-resolution → entity-scope-api → store →
  // an api slice → api-base → auth-token) into a genuine circular
  // dependency, and observably so: on the reducer object literal in
  // store.ts, one of those api slices would still be `undefined` while its
  // own module was mid-evaluation. Deferring the import until bootstrapAuth
  // actually runs (after the app's synchronous module graph has already
  // finished loading) never enters that cycle. The real fix is the two
  // module-scope `store` imports the banked item names
  // (entity-scope-api.ts:8, entity-scope.ts:21) becoming a lazy accessor.
  //
  // DV-3013 landed that accessor (src/store/get-store.ts) and tried reverting
  // this to a static import. It does not suffice: the cycle also closes
  // WITHOUT passing through store.ts at all, via entity-scope-api.ts's own
  // module-scope api-slice imports —
  //   auth-token -> entity-scope-resolution -> entity-scope-api
  //              -> orgSelectionApi -> api-base -> auth-token
  // whose symptom is `TypeError: baseQuery is not a function` at query time
  // (api-base's baseQueryWithAuth is still undefined when the slice body runs)
  // rather than the reducerPath failure the store edge produced. So the
  // dynamic import stays until those api-slice imports are inverted too.
  //
  // A dynamic import can reject for a reason a static one can't — a client on
  // a cached index.html requesting a hashed chunk a new deploy no longer
  // serves — so it gets its own catch, distinct from resolveEntityScope's own
  // (which already routes its failures through `denied`). Routed the same way
  // a denial is: to /error, whose 'unknown' variant offers a reload — exactly
  // the recovery a stale chunk needs.
  //
  // In practice this is now near-unreachable: `useScopeBootstrap` imports
  // entity-scope-resolution STATICALLY, and both its callers (Loader,
  // CallBack) are static imports in App.tsx, so Vite inlines the module into
  // the main chunk — the same bundle as this file. The catch is kept because
  // that is a bundler outcome, not a guarantee: a later `lazy()` on either
  // login surface would split it back out without anyone noticing here.
  //
  // /error itself is exempt from that redirect (below), and must be: Part
  // A's `entity: 'none'` only stops resolveEntityScope from re-resolving a
  // *denial* on /error — it has no say over this import, which runs before
  // resolveEntityScope is ever reached. Landing on /error is itself a fresh
  // document load that re-runs bootstrapAuth, so an unconditional assign
  // here would re-attempt the very chunk that just failed: on a client
  // still serving the stale index.html, that fails again, assigning to
  // /error again — a reload loop in place of the blank spinner this catch
  // was written to prevent. Keyed on the literal path, not on
  // `guard.entity === 'none'`: the other 'none' routes (/register,
  // /select-organization, /internal/*, …) have no reason to special-case a
  // chunk failure at all — they aren't where this catch's redirect ever
  // sends anyone — and reading `guard.entity` here would just be a second
  // place asserting "'none' means skip", a claim resolveEntityScope alone
  // should own. /error is the one surface this redirect can actually loop
  // back onto, so it's named directly.
  let resolveEntityScope: typeof import('./entity-scope-resolution').resolveEntityScope;
  try {
    ({ resolveEntityScope } = await import('./entity-scope-resolution'));
  } catch (err) {
    console.error('Auth bootstrap: failed to load the entity-scope step:', err);
    // Normalised, not compared raw: routeGuard tolerates a trailing slash and
    // case, so '/error/' is the same surface, and a raw comparison would miss it and
    // reassign '/error?reason=unknown' — the loop this exemption prevents.
    if (normalizeRoutePath(currentPath) === '/error') {
      // Already on the terminal surface; render it in place rather than
      // reassign to it. It needs no scope (entity: 'none'), so there is
      // nothing this step would have given it even on a successful load.
      return 'render';
    }
    window.location.assign('/error?reason=unknown');
    return 'redirecting';
  }
  const scope = await resolveEntityScope(currentPath);
  switch (scope.kind) {
    case 'ready':
    case 'switching':
      // 'switching' already completed in place via `'stay'`.
      return 'render';
    case 'navigating':
      return 'redirecting';
    case 'no-organizations':
      return 'no-organizations';
    case 'denied':
      // The trip to '/error?reason=…' is the step's (spec §3.4): it owns
      // navigation for every outcome but 'ready' and 'no-organizations', and
      // it has more than one caller now. Nothing left to do but report it.
      return 'redirecting';
  }
};
